package nl.astraeus.wiki.view

import kotlinx.html.div
import kotlinx.html.h2
import kotlinx.html.hr
import kotlinx.html.js.onClickFunction
import kotlinx.html.org.w3c.dom.events.Event
import kotlinx.html.span
import nl.astraeus.css.properties.*
import nl.astraeus.komp.HtmlBuilder
import nl.astraeus.komp.Komponent
import nl.astraeus.wiki.WikiData
import nl.astraeus.wiki.WikiData.wikiList
import nl.astraeus.wiki.css.Css.defineCss
import nl.astraeus.wiki.css.CssName
import nl.astraeus.wiki.domain.WikiListDto
import nl.astraeus.wiki.encoding.Tokenizer
import nl.astraeus.wiki.mainView
import nl.astraeus.wiki.messages.CreateWikiMessage
import nl.astraeus.wiki.messages.MessageType
import nl.astraeus.wiki.messages.UpdateWikiOrderMessage
import nl.astraeus.wiki.messages.WikiListEntryMessage
import nl.astraeus.wiki.service.UserService
import nl.astraeus.wiki.ws.sendMessage
import nl.astraeus.wiki.ws.sendMessageWithSpinner

class WikiContent : Komponent(), CssName {
  private object WikiRowCss : CssName
  private object WikiTitleCss : CssName
  private object WikiExtraInfoCss : CssName
  private object WikiCommandCss : CssName
  private object WikiPagesCss : CssName
  private object WikiModifiedCss : CssName
  private object SmallTitleCss : CssName
  private object SmallContentCss : CssName

  init {
    defineCss {
      select(WikiRowCss.cls()) {
        display(Display.grid)
        gridTemplateAreas(""" "title details commands" """)
        gridTemplateColumns(2.fr, 1.fr, 100.px)
        width(100.prc)
        //margin(0.em, 2.em)
      }
      select(WikiTitleCss.cls()) {
        gridArea("title")
        padding(0.em, 1.em)
      }
      select(WikiExtraInfoCss.cls()) {
        gridArea("details")
        fontSize(FontSize.small)
        display(Display.flex)
        flexDirection(FlexDirection.column)
        justifyContent(JustifyContent.center)
        textAlign(TextAlign.right)
      }
      select(WikiCommandCss.cls()) {
        gridArea("commands")
      }
      select(WikiPagesCss.cls()) {
      }
      // Visual hints can be added here if desired
      select(SmallTitleCss.cls()) {
        fontWeight(FontWeight._200)
      }
      select(SmallContentCss.cls()) {
        marginLeft(1.rem)
      }
      select("strong") {
        fontWeight(FontWeight._900)
      }
      select("span.inline-code") {
        whiteSpace(WhiteSpace.pre)
        fontFamily("monospace")
      }
      select("span.strike-through") {
        textDecoration("line-through")
      }

      select("table.table") {
        borderCollapse(BorderCollapse.collapse)
        borderSpacing(BorderSpacing("0"))

        select("td", "th") {
          padding(0.5.em, 1.em)
          margin(0.px)
        }
        select("thead tr") {
          backgroundColor(hsl(180, 50, 90))
          borderBottom("0.5px solid hsla(0, 0%, 30%, 0.3)")
        }
        select(".odd") {
          backgroundColor(hsl(180, 50, 95))
        }
        and(".table-bordered") {
          border("1px solid black")
          borderColor(hsl(180, 50, 95))
        }
      }

      select(".page-break") {
        borderStyle(BorderStyle.groove)
      }

      media("print") {
        select(".page-break") {
          borderStyle(BorderStyle.none)
          pageBreakAfter(PageBreak.always)
        }
      }
    }
  }

  private fun addWiki(e: Event) {
    sendMessageWithSpinner(CreateWikiMessage()) { msg ->
      check(msg.getMessageType() == MessageType.WIKI_LIST_ENTRY_MESSAGE)

      val wikiMessage = WikiListEntryMessage(msg.message)

      wikiList.add(
        WikiListDto(Tokenizer(wikiMessage.getBody()))
      )
      mainView.requestUpdate()
    }
  }

  // Generic drag & drop state reused via utility
  private val dndState = DragAndDropState<WikiListDto>()

  private fun removeWiki(wiki: WikiListDto, e: Event) {
    // prevent row click navigation
    e.stopPropagation()
    if (wiki.pages != 0) {
      // Only allow deletion when there are zero pages
      return
    }

    WikiData.removeWiki(wiki.id, wiki.title)
  }

  override fun HtmlBuilder.render() {
    div {
      div {
        span {
          +"Wiki"
        }
        if (UserService.loggedIn) {
          span(MainView.AddCss.name) {
            +"+"
            onClickFunction = ::addWiki
          }
        }
      }
      hr {}
      div {
        wikiList.forEachIndexed { index, wiki ->
          div(WikiRowCss.name) {
            div(WikiTitleCss.name) {
              h2 {
                +wiki.title
              }
            }
            div(WikiExtraInfoCss.name) {
              div(WikiPagesCss.name) {
                span(SmallTitleCss.name) {
                  +"Number of pages:"
                }
                span(SmallContentCss.name) {
                  +wiki.pages.toString()
                }
              }
              div(WikiModifiedCss.name) {
                span(SmallTitleCss.name) {
                  +"Last modified:"
                }
                span(SmallContentCss.name) {
                  +wiki.lastModified
                }
              }
            }
            if (UserService.loggedIn && wiki.pages == 0) {
              div(WikiCommandCss.name) {
                span(MainView.RemoveCss.name) {
                  +"x"
                  onClickFunction = { e ->
                    removeWiki(wiki, e)
                  }
                }
              }
            }
            // Enable drag & drop reordering when logged in (generic utility)
            dragAndDropReorder(
              index = index,
              item = wiki,
              list = wikiList,
              state = dndState,
              enabled = UserService.loggedIn,
              requestUpdate = { requestUpdate() },
              onCommit = { ordered ->
                val wikiIds = ordered.map { it.id }
                sendMessage(UpdateWikiOrderMessage(wikiIds)) { msg ->
                  // optional handling
                }
              }
            )

            onClickFunction = {
              if (!dndState.suppressClick) {
                WikiData.loadWiki(wiki.id)
              }
            }
          }
        }
      }
    }
  }
}
